import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormikContext } from "formik";
import { toast } from "react-toastify";
import { Box, HStack } from "../../../components/utils";
import BasicRepricingDetails from "./steps/BasicRepricingDetails";
import PreviewRepricingGrants from "./steps/PreviewRepricingGrants";
import VestingLevelOverrides from "./steps/VestingLevelOverrides";
import ApproveRepricing from "./steps/ApproveRepricing";
import {
  CorporateActionParams,
  OptionsRepricingDetails,
  RepricingGrantPreviewDetail,
} from "../../../types/CompanyProfile";
import { usePreviewOptionsRepricing } from "../../../queries/onBoarding";

type Props = {
  currentStep: number;
  validationSchema: Yup.ObjectSchema<any>;
  onStepChange: (step?: number) => void;
  onBackClick: () => void;
  onClose: () => void;
  readonly?: boolean;
  s3url?: string;
};

export default function OptionsRepricingNavigation({
  currentStep,
  validationSchema,
  onStepChange,
  onBackClick,
  onClose,
  readonly,
  s3url,
}: Props) {
  const formik = useFormikContext<OptionsRepricingDetails>();
  const [savedCorporateActionId, setSavedCorporateActionId] = useState<
    string | undefined
  >(formik.values.id);
  const [previewGrants, setPreviewGrants] = useState<
    RepricingGrantPreviewDetail[]
  >([]);
  const [reportBase64, setReportBase64] = useState<string | undefined>();
  const { mutate: previewRepricing, isLoading: isPreviewLoading } =
    usePreviewOptionsRepricing();
  // Refresh handling: if user lands on step 4 without a base64 (e.g. page
  // refresh), re-run preview silently to regenerate the report.
  useEffect(() => {
    if (currentStep === 4 && !reportBase64 && savedCorporateActionId) {
      previewRepricing(buildParams(formik.values), {
        onSuccess: (data) => {
          setPreviewGrants(data.grantDetails || []);
          setReportBase64(data.base64String);
        },
      });
    }
  }, [currentStep]);

  function buildParams(values: OptionsRepricingDetails): CorporateActionParams {
    return {
      id: values.id,
      name: values.name,
      date: values.date,
      type: "OPTION_REPRICING",
      ratio: 1,
      roundingMethodology: "ROUND_DOWN",
      state: values.state,
      note: values.note,
      metaData: {
        filters: {
          planIds: values.filters?.planIds,
          employmentStatus: values.filters?.employmentStatus,
          employeeType: values.filters?.employmentType,
          grantState: values.filters?.grantState,
          dateOfGrantFrom: values.filters?.grantDateFrom,
          dateOfGrantTo: values.filters?.grantDateTo,
          grantIds: values.filters?.grantIds,
        },
        repricingDetails: {
          repricingType: values.repricingType,
          showOptionsRepricingSummary: values.showOptionsRepricingSummary,
          newStrikePrice: values.newStrikePrice,
          grantStrikePriceOverrides: values.grantPriceOverrides,
          vestingStrikePriceOverrides: values.vestingLevelOverrides,
          reasonForRepricing: values.reasonForRepricing,
          boardResolutionReference: values.boardResolutionReference,
          supportingDocuments: values.supportingDocuments,
        },
      },
    };
  }

  function handlePreviewNext(errorMsg: string) {
    previewRepricing(buildParams(formik.values), {
      onSuccess: (data) => {
        setSavedCorporateActionId(data.corporateActionId);
        formik.setFieldValue("id", data.corporateActionId);
        setPreviewGrants(data.grantDetails || []);
        setReportBase64(data.base64String);
        onStepChange();
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.errorRaw || errorMsg);
      },
    });
  }

  const handleStep1Next = () => handlePreviewNext("Error generating preview");
  const handleStep2Next = () => handlePreviewNext("Error saving overrides");
  const handleStep3Next = () =>
    handlePreviewNext("Error saving vesting overrides");

  return (
    <Box className="w-full h-full pb-4 px-8 py-6">
      <HStack>
        {currentStep === 1 && (
          <BasicRepricingDetails
            validationSchema={validationSchema}
            onStepChange={readonly ? onStepChange : handleStep1Next}
            onClose={onClose}
            isLoading={readonly ? false : isPreviewLoading}
            readonly={readonly}
            s3url={s3url}
          />
        )}
        {currentStep === 2 && (
          <PreviewRepricingGrants
            savedCorporateActionId={savedCorporateActionId}
            setSavedCorporateActionId={setSavedCorporateActionId}
            previewGrants={previewGrants}
            setPreviewGrants={setPreviewGrants}
            onStepChange={readonly ? onStepChange : handleStep2Next}
            onBackClick={onBackClick}
            readonly={readonly}
            isLoading={isPreviewLoading}
          />
        )}
        {currentStep === 3 && (
          <VestingLevelOverrides
            previewGrants={previewGrants}
            onStepChange={readonly ? onStepChange : handleStep3Next}
            onBackClick={onBackClick}
            readonly={readonly}
            isLoading={readonly ? false : isPreviewLoading}
          />
        )}
        {currentStep === 4 && (
          <ApproveRepricing
            savedCorporateActionId={savedCorporateActionId}
            previewGrants={previewGrants}
            onBackClick={onBackClick}
            onClose={onClose}
            reportBase64={reportBase64}
            isRegeneratingReport={isPreviewLoading && !reportBase64}
          />
        )}
      </HStack>
    </Box>
  );
}
